/** DataBest — Breakdown grouped table / mobile cards. */
import React, { useState } from "react";
import { ChevronRight } from "lucide-react";
import RefreshingOverlay from "./RefreshingOverlay";

const fmt = (v) => (v == null ? "—" : (typeof v === "number" ? v.toFixed(2) : v));
const pct = (v) => (v == null ? "—" : `${v}%`);

const BreakdownTable = ({
    breakdown,
    groupBy,
    onGroupChange,
    loading = false,
    hideGroupSelector = false,
    title = "Breakdown",
}) => {
    const rows = breakdown?.rows || [];
    const [expanded, setExpanded] = useState({});
    const isOutcome = groupBy === "outcome";

    const toggle = (id) =>
        setExpanded((e) => ({ ...e, [id]: !e[id] }));

    const GROUPS = [
        { key: "individual", label: "Individual" },
        { key: "outcome",    label: "Outcome" },
        { key: "life_area",  label: "Life area" },
    ];

    return (
        <div
            className="relative rounded-2xl border border-slate-200 bg-white"
            data-testid="dashboard-breakdown"
        >
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 p-4">
                <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-brand-blue-950">
                    {title}
                </h3>
                {!hideGroupSelector && (
                    <div className="flex gap-1.5">
                        {GROUPS.map((g) => (
                            <button
                                key={g.key}
                                onClick={() => onGroupChange(g.key)}
                                data-testid={`group-by-${g.key}`}
                                className={`rounded-full px-3 py-1 text-[11px] font-semibold ${
                                    groupBy === g.key
                                        ? "bg-brand-blue-700 text-white"
                                        : "border border-slate-200 bg-white text-slate-600"
                                }`}
                            >
                                {g.label}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            <div className={loading ? "opacity-50 transition-opacity" : "transition-opacity"}>
            {rows.length === 0 && !loading ? (
                <div className="p-8 text-center text-sm text-slate-500">
                    No data in this period.
                </div>
            ) : (
                <>
                    {/* Desktop / tablet table */}
                    <div className="hidden sm:block overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                                <tr>
                                    <th className="px-4 py-2 text-left font-semibold">Name</th>
                                    <th className="px-2 py-2 text-right font-semibold">D</th>
                                    <th className="px-2 py-2 text-right font-semibold">Q</th>
                                    <th className="px-2 py-2 text-right font-semibold" title="Dependence = 100 − Autonomy">Dep</th>
                                    <th className="px-2 py-2 text-right font-semibold" title="(%D + %Q + (100−%Dep)) / 3">Avg Plan Score</th>
                                    <th className="px-2 py-2 text-right font-semibold">Yes %</th>
                                    <th className="px-2 py-2 text-right font-semibold" title="Records that should be scored in this period (by task frequency)">Total Records</th>
                                    <th className="px-2 py-2 text-right font-semibold" title="Completed records / total records">% Records Completed</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {rows.map((r) => {
                                    const hasKids = isOutcome && (r.activities?.length > 0);
                                    const isOpen = !!expanded[r.id];
                                    return (
                                        <React.Fragment key={r.id}>
                                            <tr
                                                data-testid={`breakdown-row-${r.id}`}
                                                className={`hover:bg-slate-50 ${hasKids ? "cursor-pointer" : ""}`}
                                                onClick={hasKids ? () => toggle(r.id) : undefined}
                                            >
                                                <td className="px-4 py-2.5 font-medium text-slate-800">
                                                    <div className="flex items-center gap-1.5">
                                                        {hasKids ? (
                                                            <button
                                                                type="button"
                                                                data-testid={`breakdown-expand-${r.id}`}
                                                                onClick={(e) => { e.stopPropagation(); toggle(r.id); }}
                                                                className="rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                                                                aria-label={isOpen ? "Collapse" : "Expand"}
                                                            >
                                                                <ChevronRight
                                                                    className={`h-4 w-4 transition-transform ${isOpen ? "rotate-90" : ""}`}
                                                                />
                                                            </button>
                                                        ) : (
                                                            isOutcome && <span className="inline-block w-5" />
                                                        )}
                                                        <span>{r.label}</span>
                                                    </div>
                                                </td>
                                                <td className="px-2 py-2.5 text-right tabular-nums text-brand-blue-700">{fmt(r.disposition)}</td>
                                                <td className="px-2 py-2.5 text-right tabular-nums text-brand-green-700">{fmt(r.quality)}</td>
                                                <td className="px-2 py-2.5 text-right tabular-nums text-violet-700">{fmt(r.dependence)}</td>
                                                <td className="px-2 py-2.5 text-right font-semibold tabular-nums text-slate-800">{fmt(r.avg_plan_score)}</td>
                                                <td className="px-2 py-2.5 text-right tabular-nums text-amber-700">{pct(r.rts_yes_pct)}</td>
                                                <td className="px-2 py-2.5 text-right tabular-nums text-slate-500">{r.total_records ?? 0}</td>
                                                <td className="px-2 py-2.5 text-right tabular-nums text-slate-500">{pct(r.records_completed_pct)}</td>
                                            </tr>
                                            {hasKids && isOpen && r.activities.map((a) => (
                                                <tr
                                                    key={a.id}
                                                    data-testid={`breakdown-activity-${a.id}`}
                                                    className="bg-slate-50/60 text-[13px]"
                                                >
                                                    <td className="py-2 pl-12 pr-4 text-slate-600">{a.label}</td>
                                                    <td className="px-2 py-2 text-right tabular-nums text-brand-blue-700">{fmt(a.disposition)}</td>
                                                    <td className="px-2 py-2 text-right tabular-nums text-brand-green-700">{fmt(a.quality)}</td>
                                                    <td className="px-2 py-2 text-right tabular-nums text-violet-700">{fmt(a.dependence)}</td>
                                                    <td className="px-2 py-2 text-right font-semibold tabular-nums text-slate-700">{fmt(a.avg_plan_score)}</td>
                                                    <td className="px-2 py-2 text-right tabular-nums text-amber-700">{pct(a.rts_yes_pct)}</td>
                                                    <td className="px-2 py-2 text-right tabular-nums text-slate-400">{a.total_records ?? 0}</td>
                                                    <td className="px-2 py-2 text-right tabular-nums text-slate-400">{pct(a.records_completed_pct)}</td>
                                                </tr>
                                            ))}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* Mobile stacked cards */}
                    <div className="divide-y divide-slate-100 sm:hidden">
                        {rows.map((r) => {
                            const hasKids = isOutcome && (r.activities?.length > 0);
                            const isOpen = !!expanded[r.id];
                            return (
                                <div
                                    key={r.id}
                                    data-testid={`breakdown-row-mobile-${r.id}`}
                                    className="p-3.5"
                                >
                                    <div
                                        className={`flex items-center justify-between ${hasKids ? "cursor-pointer" : ""}`}
                                        onClick={hasKids ? () => toggle(r.id) : undefined}
                                    >
                                        <p className="flex items-center gap-1 font-semibold text-slate-800">
                                            {hasKids && (
                                                <ChevronRight
                                                    data-testid={`breakdown-expand-mobile-${r.id}`}
                                                    className={`h-4 w-4 text-slate-400 transition-transform ${isOpen ? "rotate-90" : ""}`}
                                                />
                                            )}
                                            {r.label}
                                        </p>
                                        <p className="text-[11px] text-slate-500">
                                            {r.total_records ?? 0} rec · {pct(r.records_completed_pct)}
                                        </p>
                                    </div>
                                    <div className="mt-2 grid grid-cols-4 gap-2 text-center text-xs">
                                        <div className="rounded-lg bg-brand-blue-50 p-1.5">
                                            <p className="text-[10px] font-semibold text-brand-blue-700">D</p>
                                            <p className="font-black tabular-nums text-brand-blue-900">{fmt(r.disposition)}</p>
                                        </div>
                                        <div className="rounded-lg bg-brand-green-50 p-1.5">
                                            <p className="text-[10px] font-semibold text-brand-green-700">Q</p>
                                            <p className="font-black tabular-nums text-brand-green-900">{fmt(r.quality)}</p>
                                        </div>
                                        <div className="rounded-lg bg-violet-50 p-1.5">
                                            <p className="text-[10px] font-semibold text-violet-700">Dep</p>
                                            <p className="font-black tabular-nums text-violet-900">{fmt(r.dependence)}</p>
                                        </div>
                                        <div className="rounded-lg bg-amber-50 p-1.5">
                                            <p className="text-[10px] font-semibold text-amber-700">Yes</p>
                                            <p className="font-black tabular-nums text-amber-900">{pct(r.rts_yes_pct)}</p>
                                        </div>
                                    </div>
                                    <div className="mt-2 rounded-lg bg-slate-100 p-1.5 text-center text-xs">
                                        <span className="text-[10px] font-semibold text-slate-600">Avg Plan Score </span>
                                        <span className="font-black tabular-nums text-slate-900">{fmt(r.avg_plan_score)}</span>
                                    </div>
                                    {hasKids && isOpen && (
                                        <div className="mt-2 space-y-1.5 border-l-2 border-slate-200 pl-3">
                                            {r.activities.map((a) => (
                                                <div
                                                    key={a.id}
                                                    data-testid={`breakdown-activity-mobile-${a.id}`}
                                                    className="rounded-lg bg-slate-50 p-2"
                                                >
                                                    <div className="flex items-center justify-between">
                                                        <p className="text-[12px] font-medium text-slate-700">{a.label}</p>
                                                        <p className="text-[10px] text-slate-400">
                                                            {a.total_records ?? 0} rec · {pct(a.records_completed_pct)}
                                                        </p>
                                                    </div>
                                                    <div className="mt-1 flex gap-3 text-[11px] tabular-nums text-slate-600">
                                                        <span className="text-brand-blue-700">D {fmt(a.disposition)}</span>
                                                        <span className="text-brand-green-700">Q {fmt(a.quality)}</span>
                                                        <span className="text-violet-700">Dep {fmt(a.dependence)}</span>
                                                        <span className="font-semibold text-slate-800">APS {fmt(a.avg_plan_score)}</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </>
            )}
            </div>
            <RefreshingOverlay visible={loading} />
        </div>
    );
};

export default BreakdownTable;
